import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthFacade } from '@fantaz/shared/auth';
import { TermsAndConditionsService } from '@fantaz/shared/data-access/api';
import { UpdatedTermsDialogComponent } from '@fantaz/terms-and-conditions/ui/updated-terms-dialog';
import { ComponentStore } from '@ngrx/component-store';
import { UIStore } from '@fantaz/shared/data-access/store';
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  of,
  switchMapTo,
  tap,
  withLatestFrom,
} from 'rxjs';
import { GAEventKeys } from '@fantaz/shared/data-access/models';

interface TermsAndConditionsState {
  version?: string;
  isShowTermsAndConditionsRequestDialog: boolean;
  accepted: 'initial' | 'accepting' | 'accepted' | 'error';
  state: 'initial' | 'loading' | 'loaded' | 'error';
}

@Injectable({ providedIn: 'root' })
export class TermsAndConditionsStore extends ComponentStore<TermsAndConditionsState> {
  constructor(
    private readonly termsAndConditionsService: TermsAndConditionsService,
    private readonly dialog: MatDialog,
    private readonly authFacade: AuthFacade,
    private uiStore: UIStore,
  ) {
    super({
      isShowTermsAndConditionsRequestDialog: false,
      accepted: 'initial',
      state: 'initial',
    });
  }

  readonly isShowTermsAndConditionsRequestDialog$ = this.select(
    (s) => s.isShowTermsAndConditionsRequestDialog,
  );

  /**
   * Loading state
   */
  readonly loading$ = this.select((s) => s.state === 'loading');

  /**
   * Error loading state
   */
  readonly error$ = this.select((s) => s.state === 'error');

  /**
   * Accepted terms and conditions state
   */
  readonly accepting$ = this.select((s) => s.accepted === 'accepting');

  /**
   *
   */
  readonly accepted$ = this.select((s) => s.accepted === 'accepted');

  /**
   * Effect init to check if user has accepted latest terms and conditions
   */
  readonly init = this.effect((params$) =>
    params$.pipe(
      withLatestFrom(this.authFacade.authUser$),
      exhaustMap(([, user]) => {
        // make loading state
        this.patchState({
          state: 'loading',
          accepted: 'initial',
        });

        // fetch terms and conditions in case of user has been logged in
        if (user) {
          return this.termsAndConditionsService.fetchTermsInfo().pipe(
            tap((termsAndConditions) => {
              // make loaded state
              this.patchState({
                version: termsAndConditions.version,
                state: 'loaded',
              });

              // check if user has accepted latest terms and conditions
              let accepted = false;
              if (user && user.fantazSettings && user.fantazSettings.terms) {
                const exist = user.fantazSettings?.terms.find(
                  (term) => term.version === termsAndConditions.version,
                );
                accepted = !!exist;
              }

              if (accepted) {
                this.patchState({
                  accepted: 'accepted',
                });
                return of();
              }
              this.uiStore.reportToGoogleAnalytics({
                event: GAEventKeys.USER_NEEDS_NEW_TERMS,
                terms_and_conditions_version: termsAndConditions.version,
              });
              return this.showTermsAndConditionsRequestDialog();
            }),
          );
        }
        return of();
      }),
    ),
  );

  /**
   * Effect to accept terms and conditions
   */
  readonly acceptTermsAndConditions = this.effect((params$) =>
    params$.pipe(
      withLatestFrom(this.select((state) => state.version)),
      concatMap(([action, version]) => {
        this.patchState({ accepted: 'accepting' });
        return this.termsAndConditionsService.saveAcceptedTerms().pipe(
          tap(() => {
            this.patchState({ accepted: 'accepted' });
            this.uiStore.reportToGoogleAnalytics({
              event: GAEventKeys.USER_ACCEPTS_NEW_TERMS,
              terms_and_conditions_version: version,
            });
          }),
          catchError(() => {
            this.patchState({ accepted: 'error' });
            return of();
          }),
        );
      }),
    ),
  );

  /**
   * Effect to show new terms and conditions dialog
   */
  readonly showTermsAndConditionsRequestDialog = this.effect((params$) =>
    params$.pipe(
      switchMapTo(this.isShowTermsAndConditionsRequestDialog$),
      filter((s) => !s),
      exhaustMap(() => {
        this.patchState({
          isShowTermsAndConditionsRequestDialog: true,
        });

        // show dialog
        const dialogRef = this.dialog.open(UpdatedTermsDialogComponent, {
          closeOnNavigation: false,
          disableClose: true,
          data: {
            accepting: this.accepting$,
            accepted: this.accepted$,
            acceptButton: () => {
              this.acceptTermsAndConditions();
            },
          },
        });

        return dialogRef.afterClosed();
      }),
    ),
  );
}
